import { Icon } from "@design-system/Icon";
import { Dropdown } from "@design-system/Inputs/UniversalDropdown";
import { FlexCol, FlexRow } from "@design-system/Layout/Flex";
import { Text } from "@design-system/Typography/Text";
import { QuestionProof } from "@generated/client/graphql";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { keys, RESOURCE_NAME } from "../../i18n";

const SELECTABLE_PROOF_OPTIONS = [
    QuestionProof.Allowed,
    QuestionProof.Required,
    QuestionProof.NotAllowed,
] as const;

export const ProofSelect: FC<{
    value?: QuestionProof;
    onChange: (value: QuestionProof) => void;
    alignDropdown?: "start" | "end" | "center";
}> = ({ value, onChange, alignDropdown = "center" }) => {
    const { t } = useTranslation(RESOURCE_NAME);

    const selectValue = useMemo(() => {
        if (!value) return [];
        return [value];
    }, [value]);

    const handleChange = ([value]: string[]) => {
        onChange(value as QuestionProof);
    };

    return (
        <Dropdown
            value={selectValue}
            onValueChange={handleChange}
            defaultValue={[QuestionProof.NotAllowed]}
        >
            <Dropdown.Trigger className="h-fit w-full">
                <FlexRow
                    className="border border-tertiary bg-white focus-within:border-primary "
                    alignItems="center"
                    gap="2"
                    br="lg"
                    px="3"
                    py="1.5"
                    w="full"
                >
                    <FlexCol
                        justifyContent="between"
                        alignItems="start"
                        w="full"
                    >
                        <FlexRow>
                            <Text variant="body-sm" color="danger">
                                *
                            </Text>
                            <Text variant="body-sm" color="secondary">
                                {t(
                                    keys.create_questions_modal_options_block_proof_label,
                                )}
                            </Text>
                        </FlexRow>

                        <Text className="text-nowrap">
                            {t(
                                keys.create_questions_modal_options_block_proof_dropdown_item,
                                { context: value },
                            )}
                        </Text>
                    </FlexCol>
                    <Icon name="chevronDown" />
                </FlexRow>
            </Dropdown.Trigger>
            <Dropdown.Content align={alignDropdown} hasPortal>
                {SELECTABLE_PROOF_OPTIONS.map((proof) => (
                    <Dropdown.SelectableItem key={proof} value={proof}>
                        <Dropdown.ItemText>
                            {t(
                                keys.create_questions_modal_options_block_proof_dropdown_item,
                                { context: proof },
                            )}
                        </Dropdown.ItemText>
                        <Dropdown.SelectedIndicator />
                    </Dropdown.SelectableItem>
                ))}
            </Dropdown.Content>
        </Dropdown>
    );
};
